import { MouseEvent, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { format, isValid, parseISO } from "date-fns"
import { useQueryClient } from "@tanstack/react-query"
import { Menu, MenuItem } from "@mui/material"
import {
  RiExternalLinkLine,
  RiFilePdf2Line,
  RiLinkM,
  RiRefreshLine,
  RiUserLine,
} from "@remixicon/react"

import useRouter from "@/hooks/router/useRoute"
import IconButton from "@/components/button/IconButton"
import Tooltip from "@/components/tooltip/Tooltip"
import { createAccountStore } from "@/stores/account"
import { useActivitiesAnalysisStatusQuery } from "@/hooks/api/useActivityQuery"
import { useCopyReportLink } from "@/hooks/useCopyReportLink"
import { generateReportUrl } from "@/utils/filterPreset"
import { REPORT_TYPE } from "@/app/report/common/constants"
import { ReportStatus } from "@/types/report"
import type { ActivitiesAnalysisStatus } from "@/types/activity"

import { useQuickPdfExport } from "../../hooks/useQuickPdfExport"
import QuickPdfOffscreenRenderer from "../QuickExportPdfDialog"

/** 액티비티의 날짜(ISO datetime) → YYYY-MM-DD. 파싱 실패 시 빈 값 */
function toDateOnly(date: string): string {
  const parsed = parseISO(date ?? "")

  return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : ""
}

/** 목록 오른쪽에 쓰는 시작 시각 — 날짜는 칩에 이미 있으므로 시:분만 */
function formatStartTime(date: string): string {
  const parsed = parseISO(date ?? "")

  return isValid(parsed) ? format(parsed, "HH:mm") : ""
}

/**
 * 액티비티 → 리포트 URL 파라미터.
 * idfActivity 만 넘기면 리포트 화면이 액티비티 상세 기준으로 기간·세션·선수·템플릿을 채운다
 * (report/daily/page.tsx 의 「idfActivity만으로 진입한 경우」 경로).
 */
function activityToSearchParams(activity: ActivitiesAnalysisStatus): URLSearchParams {
  const params = new URLSearchParams()
  params.set("idfActivity", String(activity.idfActivity))

  return params
}

// 정본(web-g15-f0): Recent Report 위젯은 최근 10건만 보여준다 (#174 / ERR-039)
const RECENT_REPORT_LIMIT = 10

/**
 * 조회 범위 — 오늘 자정까지의 최근 1년.
 * Daily 리포트 액티비티 선택기(ActivitySelectorDialog)와 같은 기준이고,
 * 하루 단위로 끊어 두어 같은 날 안에서는 쿼리 키가 흔들리지 않는다.
 */
function getRecentRange() {
  const start = new Date()
  start.setFullYear(start.getFullYear() - 1)
  start.setHours(0, 0, 0, 0)

  const end = new Date()
  end.setDate(end.getDate() + 1)
  end.setHours(0, 0, 0, 0)

  return { start: start.valueOf(), end: end.valueOf() }
}

export default function RecentReportWidget() {
  const { t } = useTranslation()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyLink } = useCopyReportLink()
  const selectedIdfTeam = createAccountStore((s) => s.selectedIdfTeam)

  // 조회 이력이 아니라 분석이 끝난(ANALYZED) 액티비티를 보여준다 (ERR-044).
  // 이력 저장(POST history)이 비어 있어도 목록이 채워진다.
  const range = useMemo(getRecentRange, [])
  const { data: activities, isLoading } = useActivitiesAnalysisStatusQuery(range, true)

  const { startExport, onDataReady, isActive, isBusy } = useQuickPdfExport()

  // 행별 kebab 메뉴 anchor — 동시에 하나만 열림
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [menuEntry, setMenuEntry] = useState<ActivitiesAnalysisStatus | null>(null)

  const openMenu = useCallback(
    (event: MouseEvent<HTMLElement>, entry: ActivitiesAnalysisStatus) => {
      event.stopPropagation()
      setMenuAnchor(event.currentTarget)
      setMenuEntry(entry)
    },
    [],
  )

  const closeMenu = useCallback(() => {
    setMenuAnchor(null)
    setMenuEntry(null)
  }, [])

  const handleViewReport = useCallback(
    (entry: ActivitiesAnalysisStatus) => {
      router.push(`/report/daily?${activityToSearchParams(entry).toString()}`)
    },
    [router],
  )

  const handleCopyLink = useCallback(
    (entry: ActivitiesAnalysisStatus) => {
      const url = generateReportUrl(
        REPORT_TYPE.DAILY,
        activityToSearchParams(entry),
        selectedIdfTeam,
      )
      copyLink(url)
    },
    [copyLink, selectedIdfTeam],
  )

  const handlePdfExport = useCallback(
    (entry: ActivitiesAnalysisStatus) => {
      const date = toDateOnly(entry.date)
      const virtualPreset = {
        idfPreset: 0,
        name: entry.name,
        reportType: REPORT_TYPE.DAILY,
        idfTemplate: null as number | null,
        idfPdfTemplate: null as number | null,
        periodType: "custom" as const,
        customDateStart: date,
        customDateEnd: date,
        activityTypes: [],
        seasonWeekIds: [],
        matchDayTypes: [],
        selectedPlayerIds: [],
        dateCreated: "",
        dateUpdated: "",
      }
      startExport(virtualPreset)
    },
    [startExport],
  )

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["readActivitiesAnalysisStatus"] })
  }, [queryClient])

  // 분석 완료만, 최신순 10건
  const rows = useMemo(
    () =>
      (activities ?? [])
        .filter((activity) => activity.status === ReportStatus.Analyzed)
        .sort((a, b) => parseISO(b.date).valueOf() - parseISO(a.date).valueOf())
        .slice(0, RECENT_REPORT_LIMIT),
    [activities],
  )

  const isEmpty = !isLoading && rows.length === 0

  return (
    <div className="RecentReportWidget">
      {isLoading && (
        <div className="RecentReportWidget__loading">
          <div className="RecentReportWidget__spinner" />
        </div>
      )}

      {isEmpty && (
        <div className="RecentReportWidget__empty">
          <p>{t("noRecentReports", "No recent reports")}</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="RecentReportWidget__list">
          {rows.map((entry) => (
            <div
              key={entry.idfActivity}
              className="RecentReportWidget__row"
              onClick={() => handleViewReport(entry)}>
              <div className="RecentReportWidget__info">
                <span className="RecentReportWidget__type-badge RecentReportWidget__type-badge--daily">
                  A
                </span>
                <Tooltip title={entry.name} size="small">
                  <span className="RecentReportWidget__activity-name">{entry.name}</span>
                </Tooltip>
              </div>

              <div className="RecentReportWidget__meta">
                <span className="RecentReportWidget__date-chip">{toDateOnly(entry.date)}</span>
                {entry.playerCount > 0 && (
                  <span className="RecentReportWidget__count">
                    <RiUserLine size={14} />
                    {entry.playerCount}
                  </span>
                )}
              </div>

              <span className="RecentReportWidget__viewed">{formatStartTime(entry.date)}</span>

              <div className="RecentReportWidget__kebab" onClick={(e) => e.stopPropagation()}>
                <IconButton
                  type="ghost"
                  icon="more-vertical"
                  size="xs"
                  onClick={(e) => openMenu(e as MouseEvent<HTMLElement>, entry)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <Menu
        className="RecentReportMenu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <MenuItem
          onClick={() => {
            handleRefresh()
            closeMenu()
          }}>
          <RiRefreshLine size={16} />
          {t("refresh")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuEntry) handleViewReport(menuEntry)
            closeMenu()
          }}>
          <RiExternalLinkLine size={16} />
          {t("button.viewReport", "View Report")}
        </MenuItem>
        <MenuItem
          disabled={isBusy}
          onClick={() => {
            if (menuEntry) handlePdfExport(menuEntry)
            closeMenu()
          }}>
          <RiFilePdf2Line size={16} />
          {t("quickExport", "Download PDF")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuEntry) handleCopyLink(menuEntry)
            closeMenu()
          }}>
          <RiLinkM size={16} />
          {t("copyLink", "Copy Link")}
        </MenuItem>
      </Menu>

      <QuickPdfOffscreenRenderer active={isActive} onDataReady={onDataReady} />
    </div>
  )
}
